import {
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
} from '@angular/core';

@Directive({
  standalone: false,
  selector: '[ingsvAdmBackgroundLoader]',
})
export class BackgroundLoaderDirective implements OnChanges, OnDestroy {
  @Input('ingsvAdmBackgroundLoader') isLoading = false;
  @Input() backgroundLoaderTitle = '';

  private overlayElement!: HTMLElement;
  private titleElement!: HTMLElement;
  private isOverlayAttached = false;
  private originalPosition: string | null = null;
  private overlayUnlisteners: Array<() => void> = [];

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
  ) {
    this.createOverlay();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.backgroundLoaderTitle) {
      this.updateTitle(this.backgroundLoaderTitle);
    }
    this.toggleOverlay(this.isLoading);
  }

  ngOnDestroy(): void {
    this.detachOverlay();
    this.overlayUnlisteners.forEach(unlisten => unlisten());
    this.overlayUnlisteners = [];
  }

  private createOverlay(): void {
    this.overlayElement = this.renderer.createElement('div');
    this.renderer.addClass(this.overlayElement, 'ingsv-adm-background-loader');
    this.setOverlayStyles(this.overlayElement);
    this.blockOverlayInteractions(this.overlayElement);

    const loaderBadge = this.renderer.createElement('div');
    this.renderer.addClass(loaderBadge, 'ingsv-adm-background-loader__badge');
    this.setBadgeStyles(loaderBadge);

    const spinner = this.renderer.createElement('span');
    this.renderer.addClass(spinner, 'ingsv-adm-background-loader__spinner');
    this.setSpinnerStyles(spinner);
    this.startSpinnerAnimation(spinner);

    this.titleElement = this.renderer.createElement('span');
    this.renderer.addClass(this.titleElement, 'ingsv-adm-background-loader__title');
    this.setTitleStyles(this.titleElement);
    this.updateTitle(this.backgroundLoaderTitle);

    this.renderer.appendChild(loaderBadge, spinner);
    this.renderer.appendChild(loaderBadge, this.titleElement);
    this.renderer.appendChild(this.overlayElement, loaderBadge);
  }

  private toggleOverlay(shouldShow: boolean): void {
    if (shouldShow) {
      if (!this.isOverlayAttached) {
        const hostElement = this.elementRef.nativeElement;
        const currentPosition = window.getComputedStyle(hostElement).position;
        // jsdom may report '' instead of 'static' for the default position.
        if (!currentPosition || currentPosition === 'static') {
          this.originalPosition = hostElement.style.position;
          this.renderer.setStyle(hostElement, 'position', 'relative');
        }
        this.renderer.appendChild(hostElement, this.overlayElement);
        this.isOverlayAttached = true;
      }
      return;
    }

    this.detachOverlay();
  }

  private detachOverlay(): void {
    if (!this.isOverlayAttached) {
      return;
    }

    const hostElement = this.elementRef.nativeElement;
    this.renderer.removeChild(hostElement, this.overlayElement);
    if (this.originalPosition !== null) {
      if (this.originalPosition) {
        this.renderer.setStyle(hostElement, 'position', this.originalPosition);
      } else {
        this.renderer.removeStyle(hostElement, 'position');
      }
      this.originalPosition = null;
    }
    this.isOverlayAttached = false;
  }

  private setOverlayStyles(element: HTMLElement): void {
    this.renderer.setStyle(element, 'position', 'absolute');
    this.renderer.setStyle(element, 'inset', '0');
    this.renderer.setStyle(element, 'z-index', '20');
    this.renderer.setStyle(element, 'display', 'flex');
    this.renderer.setStyle(element, 'align-items', 'center');
    this.renderer.setStyle(element, 'justify-content', 'center');
    this.renderer.setStyle(element, 'background', 'rgba(255, 255, 255, 0.72)');
    this.renderer.setStyle(element, 'border-radius', '10px');
    this.renderer.setStyle(element, 'pointer-events', 'auto');
    this.renderer.setStyle(element, 'touch-action', 'none');
  }

  private setBadgeStyles(element: HTMLElement): void {
    this.renderer.setStyle(element, 'display', 'inline-flex');
    this.renderer.setStyle(element, 'align-items', 'center');
    this.renderer.setStyle(element, 'gap', '12px');
    this.renderer.setStyle(element, 'padding', '10px 16px');
    this.renderer.setStyle(element, 'border-radius', '999px');
    this.renderer.setStyle(element, 'border', '1px solid #dfe3e8');
    this.renderer.setStyle(element, 'background-color', '#ffffff');
    this.renderer.setStyle(element, 'box-shadow', '0 2px 8px rgba(20, 34, 66, 0.14)');
  }

  private setSpinnerStyles(element: HTMLElement): void {
    this.renderer.setStyle(element, 'width', '34px');
    this.renderer.setStyle(element, 'height', '34px');
    this.renderer.setStyle(element, 'border-radius', '50%');
    this.renderer.setStyle(element, 'border', '4px solid rgba(42, 59, 104, 0.22)');
    this.renderer.setStyle(element, 'border-top-color', '#2A3B68');
  }

  private setTitleStyles(element: HTMLElement): void {
    this.renderer.setStyle(element, 'font-size', '17px');
    this.renderer.setStyle(element, 'font-weight', '700');
    this.renderer.setStyle(element, 'color', '#1a2138');
  }

  private updateTitle(title: string): void {
    const safeTitle = (title || '').trim();
    this.renderer.setProperty(this.titleElement, 'textContent', safeTitle);

    if (safeTitle) {
      this.renderer.setStyle(this.titleElement, 'display', 'inline');
      return;
    }

    this.renderer.setStyle(this.titleElement, 'display', 'none');
  }

  private startSpinnerAnimation(element: HTMLElement): void {
    if (typeof element.animate !== 'function') {
      return;
    }

    element.animate(
      [
        { transform: 'rotate(0deg)' },
        { transform: 'rotate(360deg)' },
      ],
      {
        duration: 800,
        easing: 'linear',
        iterations: Number.POSITIVE_INFINITY,
      },
    );
  }

  private blockOverlayInteractions(element: HTMLElement): void {
    const blockedEvents = [
      'click',
      'mousedown',
      'mouseup',
      'pointerdown',
      'pointerup',
      'touchstart',
      'touchend',
    ];
    this.overlayUnlisteners = blockedEvents.map(eventName =>
      this.renderer.listen(element, eventName, (event: Event) => {
        event.preventDefault();
        event.stopPropagation();
      }),
    );
  }
}
